import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Stars, Badge } from "@/lib/ui";
import { Briefcase, MapPin, Phone } from "lucide-react";
import TrustBadges from "@/components/TrustBadges";
import { AvailabilitySummary } from "@/components/AvailabilityEditor";
import Avatar from "@/components/Avatar";
import { ThankYouCard } from "@/components/ThankYou";
import BadgesWall from "@/components/BadgesWall";

export default function HelperProfile() {
  const { id } = useParams();
  const [h, setH] = useState(null);

  useEffect(() => { api.get(`/helpers/${id}`, { params: { tz_offset: new Date().getTimezoneOffset() } }).then((r) => setH(r.data)); }, [id]);
  if (!h) return <div className="max-w-3xl mx-auto px-4 py-16 text-lg">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <div className="bg-white border border-[#E2D9CE] rounded-2xl p-6 sm:p-8 shadow-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <Avatar user={h} size="xl" />
          <div>
            <h1 className="text-3xl font-display font-bold text-slate-900">{h.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Stars rating={h.avg_rating} size="text-lg" />
              <span className="text-slate-500">{h.avg_rating} · {h.review_count} reviews</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {h.is_available ? <Badge color="green" testid="available-tag">Available now</Badge> : <Badge color="slate" testid="unavailable-tag">Unavailable</Badge>}
              <TrustBadges user={h} size="lg" />
              <Badge color="gold"><Briefcase className="w-4 h-4" /> {h.completed_jobs} jobs completed</Badge>
            </div>
          </div>
        </div>

        <p className="text-lg text-slate-700 leading-relaxed mt-6">{h.bio || "A kind helper on Kindred."}</p>
        <div className="mt-4 space-y-2 text-slate-700">
          {h.address && <p className="flex items-center gap-2"><MapPin className="w-5 h-5 text-[#2E7D6B]" /> {h.address}</p>}
          {h.phone && <p className="flex items-center gap-2"><Phone className="w-5 h-5 text-[#2E7D6B]" /> {h.phone}</p>}
        </div>
        {h.skills?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {h.skills.map((s) => <Badge key={s} color="slate">{s}</Badge>)}
          </div>
        )}
        <div className="mt-6 pt-5 border-t border-[#E2D9CE]">
          <h3 className="font-semibold text-slate-900 mb-2">Availability</h3>
          <AvailabilitySummary availability={h.availability} />
        </div>
      </div>

      <BadgesWall badges={h.badges} />

      {h.thank_you_notes?.length > 0 && (
        <section data-testid="thank-you-wall">
          <h2 className="text-2xl font-display font-bold text-slate-900 mt-10 mb-4">Thank-you notes ({h.thank_you_notes.length})</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {h.thank_you_notes.map((n) => <ThankYouCard key={n.id} note={n} compact />)}
          </div>
        </section>
      )}

      <h2 className="text-2xl font-display font-bold text-slate-900 mt-10 mb-4">Reviews</h2>
      {h.reviews.length === 0 ? (
        <p className="text-slate-600">No reviews yet.</p>
      ) : (
        <div className="space-y-4">
          {h.reviews.map((r) => (
            <div key={r.id} className="bg-white border border-[#E2D9CE] rounded-2xl p-5">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-slate-900">{r.seeker_name}</p>
                <Stars rating={r.rating} />
              </div>
              {r.comment && <p className="text-slate-700 mt-2">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
